<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from "vue";
import type { CmsElementCategoryInfo } from "~/composables/useCustom";
import { getSanitizeUrl } from "~/components/utilities";

const props = defineProps<{
  content: CmsElementCategoryInfo;
}>();

const {
  button,
  flattext,
  headline,
  video_link,
  subheadline,
  video_upload,
  coverimage_video,
} = props.content?.config ?? {};

const showVideo = ref(false);
const videoRef = ref<HTMLVideoElement | null>(null);

// Function to detect if the video_link is a YouTube URL
const isYouTube = computed(() => {
  return (
    video_link?.value?.url?.includes("youtube.com") ||
    video_link?.value?.url?.includes("youtu.be")
  );
});

// Convert YouTube link to embeddable format
const youtubeThumbNails = ref<any>(null);
const getYouTubeEmbedUrl = computed(() => {
  return `https://www.youtube.com/embed/` + getUrl() + `?autoplay=1&rel=0`;
});

const getUrl = () => {
  if (!video_link?.value?.url) return "";
  const url = new URL(video_link.value.url);
  if (url.hostname.includes("youtu.be")) {
    return url.pathname.slice(1);
  }
  return url.searchParams.get("v");
};

const getYoutubeThumbnailUrl = (videoId: any) => {
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/0.jpg`;
  }
};

const playVideo = () => {
  showVideo.value = true;
  setTimeout(() => {
    videoRef.value?.play();
  }, 100);
};

// const coverImageRef = ref(null);
const videoHeight = ref(205);
onMounted(async () => {
  const youtubeId = getUrl();
  youtubeThumbNails.value = getYoutubeThumbnailUrl(youtubeId);

  const imageElement = document.querySelector("#coverImageRef");
  if (imageElement) {
    videoHeight.value = (imageElement as HTMLElement).clientHeight;
  }
});
</script>

<template>
  <section class="section section-main my-5">
    <div class="section__body">
      <div class="about-us-video-banner container">
        <div class="row align-items-center section--solid">
          <div class="col-12 col-md-7">
            <div class="about-us-video-content">
              <h2>{{ headline?.value }}</h2>
              <h5 class="mb-5 about-us-video-header">{{ subheadline?.value }}</h5>
              <p>
                {{ flattext?.value }}
              </p>
              <div class="product__actions">
                <NuxtLink
                  v-if="button?.value?.title"
                  :to="getSanitizeUrl(button.value?.url)"
                  :target="button.value?.target"
                  class="btn btn-default btn-primary product__btn mt-4 rounded-3 py-3"
                >
                  {{ button.value?.title }}
                  <i class="ico-arrow-right-primary">
                    <img
                      src="assets/images/svg/arrow-right-primary.svg"
                      alt="arrow"
                      width="20"
                      height="13"
                    />
                  </i>
                </NuxtLink>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-5 px-0">
            <div
              v-if="video_link?.value?.url || video_upload?.value?.url"
              class="relative"
            >
              <!-- Show cover image initially -->
              <template v-if="!showVideo">
                <NuxtImg
                  id="coverImageRef"
                  v-if="coverimage_video?.value?.url"
                  format="webp"
                  loading="lazy"
                  :src="coverimage_video?.value?.url"
                  :alt="
                    coverimage_video?.value?.alt ||
                    coverimage_video?.value?.fileName
                  "
                  :width="coverimage_video?.value?.metaData?.width || 176"
                  :height="coverimage_video?.value?.metaData?.height || 205"
                  class="w-100 about-us-video-cover"
                  @click="playVideo"
                />

                <NuxtImg
                  v-else-if="youtubeThumbNails"
                  format="webp"
                  loading="lazy"
                  :src="youtubeThumbNails"
                  :width="176"
                  :height="205"
                  class="w-100 about-us-video-cover"
                  alt="Cover Image"
                  @click="playVideo"
                />
              </template>

              <!-- Show YouTube video if detected -->
              <iframe
                v-if="showVideo && isYouTube"
                :src="getYouTubeEmbedUrl"
                class="w-100 about-us-video-cover"
                frameborder="0"
                :height="videoHeight || 205"
                allow="autoplay; encrypted-media"
                allowfullscreen
                style="margin-bottom: -3px"
              ></iframe>

              <!-- Show uploaded MP4 video -->
              <div
                v-else-if="showVideo && video_upload?.value?.url"
                class="video-background"
                :style="{
                  height: videoHeight ?? 205 + 'px',
                }"
              >
                <video
                  ref="videoRef"
                  controls
                  class="w-100 about-us-video-cover"
                >
                  <source :src="video_upload.value.url" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <NuxtImg
              v-else-if="coverimage_video?.value?.url"
              format="webp"
              loading="lazy"
              :src="coverimage_video?.value?.url"
              :alt="
                coverimage_video?.value?.alt || coverimage_video?.value?.fileName
              "
              :width="coverimage_video?.value?.metaData?.width || 176"
              :height="coverimage_video?.value?.metaData?.height || 205"
            ></NuxtImg>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
